/**
 * The payment verification queue (S-A50) — the highest-frequency finance screen.
 *
 * ===========================================================================
 * WHY THIS SCREEN IS CAREFUL
 * ===========================================================================
 * Verification is the most consequential route in the product, for two reasons the
 * backend states plainly:
 *
 * 1. IT IS WHERE THE TWO-PERSON CONTROL APPLIES. Whoever requested the payment does
 *    not confirm it arrived. If the caller is the requester and no other qualified
 *    approver exists, the server answers 409 `second_person_required` with a reason
 *    written for the user. We show that reason VERBATIM. It is actionable and
 *    discloses nothing they cannot already see.
 *
 * 2. A DUPLICATE DOES LASTING DAMAGE. An over-credited invoice looks settled and
 *    stops being chased. `Idempotency-Key` is therefore REQUIRED, not optional, and
 *    the key is minted once per user-initiated attempt and reused across retries of
 *    that same attempt — a fresh key per retry would defeat the whole mechanism.
 *
 * And T-1: a one-person action is never represented as a two-person approval. If the
 * response's `control` block says `single_practitioner`, this screen says so too.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  newIdempotencyKey,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type Payment,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Drawer,
  Modal,
  Select,
  StatusPill,
  Table,
  Textarea,
  statusTone,
  useToast,
  type Column,
} from '@legalcrm/ui';
import { useEffect, useRef, useState } from 'react';

import { Can } from '@/auth/permissions';
import { formatDateTime, formatMoney, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

/**
 * The statuses a payment can be filtered to.
 *
 * `awaiting` is not a status -- it is the set of three that mean "a client says they
 * have paid and nobody has confirmed it arrived". It exists because the dashboard's
 * "Payments to verify" tile counts exactly that set, while this filter used to mean
 * `proof_submitted` alone under the same words. A payment sitting at `pending` was
 * therefore counted by the tile and hidden by the list, and the two screens
 * contradicted each other about the same queue.
 *
 * Keeping the individual statuses below it matters: "why is this one waiting" is a
 * different question from "what is waiting", and the answer is the status.
 */
const AWAITING_SET = ['pending', 'proof_submitted', 'under_review'];

const STATUS_OPTIONS = [
  { value: 'awaiting', label: 'Awaiting verification' },
  { value: 'proof_submitted', label: 'Proof submitted' },
  { value: 'under_review', label: 'Under review' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All payments' },
];

const PAYMENT_TONES: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  verified: 'success',
  proof_submitted: 'warning',
  under_review: 'info',
  pending: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
  refunded: 'neutral',
  partially_refunded: 'warning',
};

export function PaymentQueuePage() {
  const [status, setStatus] = useState('awaiting');
  const [selected, setSelected] = useState<Payment | null>(null);

  const list = usePaginatedList<Payment>(qk.list('payments', { status }), STAFF.payments, {
    // The API takes `status` repeatably, so the set goes over as three values rather
    // than needing a server-side alias for the word "awaiting".
    status: status === 'awaiting' ? AWAITING_SET : status || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [status, resetCursor]);

  const columns: Column<Payment>[] = [
    {
      key: 'amount',
      header: 'Amount',
      render: (row) => (
        <span className="font-medium tabular-nums text-ink">
          {formatMoney(row.amount, row.currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusPill label={humanise(row.status)} tone={PAYMENT_TONES[row.status] ?? statusTone(row.status)} />
      ),
    },
    {
      key: 'method',
      header: 'Method',
      hideOnMobile: true,
      render: (row) => <span className="text-ink-muted">{humanise(row.method)}</span>,
    },
    {
      key: 'reference',
      header: 'Reference',
      hideOnMobile: true,
      render: (row) => (
        <span className="font-mono text-sm text-ink-muted">{row.reference || '—'}</span>
      ),
    },
    {
      key: 'received_at',
      header: 'Received',
      align: 'right',
      hideOnMobile: true,
      render: (row) => (
        <span className="tabular-nums">{formatDateTime(row.received_at)}</span>
      ),
    },
    {
      key: 'verified_by',
      header: 'Verified by',
      align: 'right',
      render: (row) =>
        row.verified_by ? (
          <Badge tone="success" size="sm">
            Verified
          </Badge>
        ) : (
          <span className="text-ink-subtle">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Payments"
        description="Proof of payment waiting to be checked against the invoice."
      />

      <div className="mb-3 max-w-xs">
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <Table
        caption="Payment queue"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={setSelected}
        isRowActive={(row) => row.id === selected?.id}
        emptyTitle="Nothing waiting"
        emptyDescription="No payments match this filter."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />

      <PaymentPanel payment={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function PaymentPanel({ payment, onClose }: { payment: Payment | null; onClose: () => void }) {
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  if (!payment) return null;

  // `pending` is included alongside `proof_submitted`/`under_review`: the backend's
  // `verify_payment()` has no status precondition — a payment staff already knows about
  // (e.g. confirmed against a bank statement, recorded straight from the invoice) can be
  // verified with no client-submitted proof at all. Excluding `pending` here left every
  // staff-recorded payment with no path to Verified anywhere in the app, even though the
  // queue's own "awaiting verification" filter already lists it as needing action.
  const awaiting =
    payment.status === 'pending' ||
    payment.status === 'proof_submitted' ||
    payment.status === 'under_review';

  return (
    <>
      <Drawer
        open={Boolean(payment)}
        onClose={onClose}
        title={formatMoney(payment.amount, payment.currency)}
        description={`${humanise(payment.method)} · ${payment.reference || 'no reference'}`}
        size="md"
        footer={
          awaiting ? (
            <>
              <Can module="payment" action="reject">
                <Button variant="secondary" onClick={() => setRejecting(true)}>
                  Reject
                </Button>
              </Can>
              {/* Only Finance and Org Admin hold `payment.verify`. Whoever requests
                  money does not confirm it arrived. */}
              <Can module="payment" action="verify">
                <Button variant="primary" onClick={() => setVerifying(true)}>
                  Verify payment
                </Button>
              </Can>
            </>
          ) : null
        }
      >
        <dl className="flex flex-col gap-3">
          <Row label="Status">
            <StatusPill
              label={humanise(payment.status)}
              tone={PAYMENT_TONES[payment.status] ?? 'neutral'}
            />
          </Row>
          <Row label="Received">{formatDateTime(payment.received_at)}</Row>
          <Row label="Verified">{formatDateTime(payment.verified_at)}</Row>
        </dl>

        {/*
          `requested_by` and `verified_by` are the EVIDENCE for the two-person
          control. Showing them is the point: a firm should be able to see, on the
          record, that two different people were involved.
        */}
        <Card className="mt-4">
          <CardBody>
            <h4 className="mb-2 text-sm font-semibold text-ink">Two-person control</h4>
            <dl className="flex flex-col gap-2">
              <Row label="Requested by">
                <span className="font-mono text-xs">{payment.requested_by ?? '—'}</span>
              </Row>
              <Row label="Verified by">
                <span className="font-mono text-xs">{payment.verified_by ?? '—'}</span>
              </Row>
            </dl>
            {payment.control?.single_practitioner && (
              <p className="mt-3 rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink">
                <strong className="font-semibold">Performed by one person.</strong> This firm
                operates in single-practitioner mode and no second qualified approver was
                available. This is recorded as a single-person action, not as a two-person
                approval.
              </p>
            )}
          </CardBody>
        </Card>
      </Drawer>

      <VerifyDialog
        open={verifying}
        onClose={() => setVerifying(false)}
        payment={payment}
        onDone={onClose}
      />
      <RejectDialog
        open={rejecting}
        onClose={() => setRejecting(false)}
        payment={payment}
        onDone={onClose}
      />
    </>
  );
}

function VerifyDialog({
  open,
  onClose,
  payment,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  payment: Payment;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [reference, setReference] = useState(payment.reference ?? '');

  /**
   * ONE KEY PER USER-INITIATED ATTEMPT, held across retries of that attempt.
   *
   * Minting a fresh key on each retry would defeat idempotency entirely — the second
   * attempt would look like a second payment. The key is reset when the dialog is
   * reopened, which is a genuinely new attempt.
   */
  const idempotencyKey = useRef(newIdempotencyKey());
  useEffect(() => {
    if (open) idempotencyKey.current = newIdempotencyKey();
  }, [open]);

  const verify = useApiMutation<Payment, { reference: string }>(
    {
      mutationFn: (body) =>
        api.post<Payment>(STAFF.paymentVerify(payment.id), body, {
          idempotencyKey: idempotencyKey.current,
        }),
      invalidates: [qk.all('payments'), qk.all('invoices')],
    },
    {
      onSuccess: (updated) => {
        // T-1: never represent a one-person action as a two-person approval.
        toast({
          title: updated.control?.single_practitioner
            ? 'Verified under single-practitioner mode'
            : 'Payment verified',
          description: updated.control?.single_practitioner
            ? 'Recorded as a single-person action, because no other qualified approver was available.'
            : `${formatMoney(updated.amount, updated.currency)} confirmed against the invoice.`,
          tone: updated.control?.single_practitioner ? 'warning' : 'success',
        });
        onClose();
        onDone();
      },
      onError: (error) => {
        toast({
          title: 'Verification refused',
          // The 409 detail is written for the user — "no other qualified approver
          // is available", "the requester cannot verify their own payment". Show it
          // exactly as given; paraphrasing loses the reason and the next step.
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The payment could not be verified. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Verify this payment"
      description="Confirm the money has arrived in the firm's account."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={verify.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={verify.isPending}
            onClick={() => verify.mutate({ reference })}
          >
            Verify {formatMoney(payment.amount, payment.currency)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Every state-changing action shows what will happen before it happens. */}
        <div className="rounded border bg-surface-sunken px-3 py-2 text-sm text-ink">
          <p className="font-medium">This will:</p>
          <ul className="mt-1 list-inside list-disc text-ink-muted">
            <li>credit {formatMoney(payment.amount, payment.currency)} against the invoice</li>
            <li>issue a receipt to the client</li>
            <li>record you as the verifier, permanently</li>
          </ul>
        </div>

        <Textarea
          label="Bank reference"
          hint="What appears on the firm's statement. Recorded with the verification."
          rows={2}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function RejectDialog({
  open,
  onClose,
  payment,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  payment: Payment;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [clientReason, setClientReason] = useState('');
  const [internalReason, setInternalReason] = useState('');

  const reject = useApiMutation<Payment, { client_reason: string; internal_reason: string }>(
    {
      mutationFn: (body) => api.post<Payment>(STAFF.paymentReject(payment.id), body),
      invalidates: [qk.all('payments'), qk.all('invoices')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Payment rejected', tone: 'success' });
        setClientReason('');
        setInternalReason('');
        onClose();
        onDone();
      },
      onError: (error) => {
        toast({
          title: 'Could not reject',
          description:
            error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reject this payment"
      description="Send the proof back with something the client can act on."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!clientReason.trim()}
            loading={reject.isPending}
            onClick={() =>
              reject.mutate({
                client_reason: clientReason.trim(),
                internal_reason: internalReason.trim(),
              })
            }
          >
            Reject payment
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          TWO REASONS, TWO FIELDS, AND THEY GO TO DIFFERENT PEOPLE.
          The client reason is mandatory: a rejection the client cannot act on tells
          them only that their money has not been recognised.
        */}
        <div className="rounded-lg border border-client-facing/40 bg-client-facing-subtle/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-client-facing">
            The client will read this
          </p>
          <Textarea
            label="Reason for the client"
            hint="Plain language, and something they can act on — what was wrong and what to send instead."
            rows={3}
            required
            value={clientReason}
            onChange={(event) => setClientReason(event.target.value)}
          />
        </div>

        <div className="rounded-lg border border-internal/40 bg-internal-subtle/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-internal">
            Internal only — never shown to the client
          </p>
          <Textarea
            label="Internal note"
            hint="Optional. For the firm's own record."
            rows={2}
            value={internalReason}
            onChange={(event) => setInternalReason(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
